import React, { useEffect, useState } from "react";
import { RequestType } from "../../../models/requestType";
import { FieldGroupRow, FieldGroupRowStatus, RequestAnalysisData, GroundingState } from "../../../models/intake";
import FormConstants from "./DataEntryFieldsConstants";
import { writeFieldToForm, ClassicFieldMeta, toDropdownFieldMeta } from "./formFieldHelpers";
import DropdownField from "../../../common/formFields/DropdownField";
import { isMappedPurposeUsable } from "./requestTypePurposeResolver";

const DEFAULT_DROPDOWN_VALUE = 0;

export interface RequestTypePurposeFieldProps {
    readonly requestTypes?: readonly RequestType[];
    /** Currently selected Request Type id (read from DATA_ENTRY_FIELDS_FORM). */
    readonly value?: number;
    /** Currently selected Request Purpose id (read from DATA_ENTRY_FIELDS_FORM). */
    readonly purposeValue?: number;
    /**
     * True while the value is still the AI's original suggestion and the user
     * has not overridden it - drives the "AI pre-selected" chip (AC11). Once
     * the user changes either dropdown this component locally treats the row
     * as edited and stops rendering the chip, independent of this prop, so a
     * parent that never re-renders with isAiPreSelected=false still gets
     * correct chip behavior.
     */
    readonly isAiPreSelected: boolean;
    /**
     * True when the API resolved a valid request Type but the extracted purpose was
     * present-yet-not-a-valid-child of it — the "amber / purpose-uncertain" state (TDD §11). Renders
     * the amber ⚠ hint on the Purpose dropdown (Type stays green) prompting the specialist to pick a
     * purpose; nothing is auto-selected. Suppressed once the user picks a purpose or edits the row.
     * Exact-only, deterministic — distinct from F1c's patient/requestor multiple-match yellow.
     */
    readonly purposeUncertain?: boolean;
    /**
     * AB#35075: the advisory SECOND request-type candidate the document also supported, e.g.
     * "Internal / Patient Financial Services" when the primary is "Insurance / Denial". Drives the
     * amber state + its explanatory tooltip. Never auto-selected; undefined for an unambiguous
     * document, which is the common case.
     */
    readonly secondaryTypeLabel?: string;
    /**
     * AB#35075: true when the extraction returned a second plausible type, even if its display name
     * could not be resolved from this user's request-type list. The hint must not silently disappear
     * just because the label is unknown - that is how the amber reason ended up invisible.
     */
    readonly secondaryPresent?: boolean;
    /**
     * AB#37979 (David Heifetz, PR !9995 Important 3): the TYPE's grounding verdict is
     * InferredNoSource - present and catalog-mapped, but not verifiable against a located span.
     *
     * This prop exists because `statusExplanation` cannot reach this row. QuickEntryFieldGroupList
     * renders that ⓘ only in its DEFAULT row markup, and this row supplies `renderRowContent`, so it
     * takes the custom-content branch and the explanation was never displayed. AB#35075 hit the same
     * wall and solved it by surfacing its reason through field props instead - this follows that.
     */
    readonly typeUngrounded?: boolean;
    /**
     * The free-text request purpose the AI extracted (e.g. "Attorney"), used only to compose the
     * amber hint's explanatory tooltip. Not a selectable value. Undefined when the document stated
     * no purpose at all (the tooltip then explains that none was found).
     */
    readonly extractedPurpose?: string;
    /** Raw AI-extracted request Type free-text (e.g. "Some Unlisted Org"). */
    readonly extractedType?: string;
    /**
     * True when the AI extracted a request Type that did NOT resolve to an exact ROIS catalog match.
     * Per confirmed design we still show the extracted free text — in red, as a non-selectable hint —
     * so the specialist sees what was found; it can't be auto-selected because it isn't a catalog option.
     */
    readonly typeNotExact?: boolean;
    readonly dispatch: Function;
    readonly formName?: string;
    /**
     * Extra, non-form side effects the caller wants to run on a Request Type
     * change (e.g. classic RightPane's sensitive-info reset / barcode DDS
     * trigger). The base DATA_ENTRY_FIELDS_FORM writes are always handled by
     * this component itself via writeFieldToForm - callers should NOT also
     * dispatch a redundant form write here.
     */
    readonly onRequestTypeChange?: (newType: number) => void;
    readonly onRequestPurposeChange?: (newPurpose: number) => void;
    /**
     * Real redux-form validation state for the Request Type dropdown,
     * threaded down from DataEntryDetailsRightPane.tsx (Spec #34102 / F1d
     * regression fix - restores the "Request Type is required." text on a
     * blocked Next-click, lost when this component stopped being wrapped in
     * a real <Field> during the F1a extraction). Omitted entirely by Quick
     * Entry's own call site - it has its own FieldGroupRowStatus gating.
     */
    readonly meta?: ClassicFieldMeta;
    /** Same as `meta`, for the Purpose dropdown ("Purpose Type is required."). */
    readonly purposeMeta?: ClassicFieldMeta;
    /**
     * Renders master-demo-v6.html's compact, side-by-side "Type"/"Purpose"
     * columns (short field titles, no required asterisk - required-ness is
     * already conveyed by the row's own status icon/color) instead of
     * Classic Mode's stacked full-question-label layout (Spec #34102 / F1d
     * bug fix - live-testing found Quick Entry rendering Classic Mode's
     * layout verbatim: "What is the request type?"/"What is the purpose of
     * the request?" stacked full-width with red asterisks, instead of the
     * compact "Type"/"Purpose" side-by-side columns the handoff mockup's
     * .qe-row--editing .qe-row__value--cols rule specifies). Omitted by
     * Classic Mode's own call site (DataEntryDetailsRightPane.tsx), so its
     * rendering is completely unaffected.
     */
    readonly quickEntryCompact?: boolean;
}

/**
 * Shared Request Type / Purpose field (Spec #34098 / F1a, AC11). Rendered by
 * both the classic RightPane Details tab and the new Quick Entry row so both
 * views write into - and read from - the same DATA_ENTRY_FIELDS_FORM
 * redux-form instance via the shared writeFieldToForm helper.
 */
/**
 * AB#37979: the reason text for the ungrounded-type hint. A module constant so the rendered hint and
 * the mapper's row explanation cannot drift apart in wording.
 */
export const UNGROUNDED_HINT_TOOLTIP =
    "The request type is in the list, but the document does not verifiably state it - "
    + "no cited passage could be located and checked. Confirm it against the document, "
    + "or pick the correct type.";

/**
 * AB#38739: the reason this row is amber when nothing specific is wrong with the extraction - the
 * standing rule that these two fields are always reviewed. A module constant for the same reason
 * UNGROUNDED_HINT_TOOLTIP is one: the rendered hint and the mapper's row explanation must not drift
 * apart in wording.
 */
export const ALWAYS_REVIEW_EXPLANATION =
    "Request type and purpose are always reviewed before submission, whatever the extraction "
    + "reported - they decide how this request is routed and billed. The pre-selected values are "
    + "the AI's reading of this document; check them against the request and confirm.";

const RequestTypePurposeField: React.FC<RequestTypePurposeFieldProps> = (props) => {
    // Review round 2 (adversarial sweep, PR !10065). The latch must survive a REMOUNT, because this
    // component now unmounts in normal use: acknowledging a row collapses it to the read-only display,
    // and [Edit] mounts a fresh instance. Before this PR an amber row's isEditing was permanently true,
    // so the field never unmounted and a mount-local `false` was safe.
    //
    // Seeded from the parent's own verdict instead. isAiPreSelected is computed in
    // mapRequestTypePurposeToFieldGroupRow from editedFromAi - it is already false once the live form
    // value diverges from the AI ids - so a row the specialist has changed remounts with the latch set
    // and the AB#37979 ungrounded warning, the AB#35075 "Also matched ..." hint and the amber styling
    // stay suppressed over the value they chose, rather than re-arming behind their back.
    const [edited, setEdited] = useState(() => Boolean(props.value) && !props.isAiPreSelected);

    // A fresh AI suggestion (e.g. a new transaction loaded) should re-arm the chip and the hints.
    //
    // AB#37976 (review round 2): this used to key off props.isAiPreSelected, which was a valid proxy
    // for "a fresh AI suggestion" only while that flag could not move in response to the user. It can
    // now - the round-1 chip fix added liveTypeContradictsAi to the mapper, so picking a different Type
    // flips isAiPreSelected within the same interaction that set this latch, and the effect promptly
    // cleared it. That re-armed the AB#35075 amber "Also matched ..." hint over a Type the specialist
    // had just chosen, and re-selecting the AI's own Type flipped the flag back to true and brought the
    // AB#35074 chip back. Key off the RAW AI-extracted free text instead: it comes straight from the
    // extraction payload, so it changes when a new transaction's analysis arrives and never in response
    // to a dropdown change. Undefined on the Classic/legacy call sites, where the effect is then a
    // mount-only no-op - the latch already starts false.
    useEffect(() => {
        setEdited(false);
    }, [props.extractedType, props.extractedPurpose]);

    const formName = props.formName ?? FormConstants.DATA_ENTRY_FIELDS_FORM;
    const selectedType = props.requestTypes?.find(type => type.id === props.value);

    const onChangeType = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newType = parseInt(event.target.value, 10);
        writeFieldToForm(props.dispatch, FormConstants.REQUEST_TYPE, newType, formName);
        writeFieldToForm(props.dispatch, FormConstants.PURPOSE_TYPE, DEFAULT_DROPDOWN_VALUE, formName);
        setEdited(true);
        props.onRequestTypeChange?.(newType);
    };

    const onChangePurpose = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPurpose = parseInt(event.target.value, 10) || DEFAULT_DROPDOWN_VALUE;
        writeFieldToForm(props.dispatch, FormConstants.PURPOSE_TYPE, newPurpose, formName);
        setEdited(true);
        props.onRequestPurposeChange?.(newPurpose);
    };

    const showChip = props.isAiPreSelected && !edited;
    // Amber purpose-uncertain hint: only while the specialist hasn't yet picked a purpose (no live
    // value) and hasn't edited the row. A valid Type is a precondition set upstream (the API only
    // flags purposeUncertain when a type resolved), so Type renders normally (green) here.
    const showPurposeUncertain = (props.purposeUncertain ?? false) && !edited && !props.purposeValue;
    // AB#35075: amber because the DOCUMENT supported two plausible types - distinct from the
    // purpose-uncertain amber above, so it has its own hint and tooltip. Suppressed once the
    // specialist edits the row: from then on the selection is theirs.
    const showSecondaryCandidate = (Boolean(props.secondaryTypeLabel) || Boolean(props.secondaryPresent)) && !edited;
    // Same suppression as the secondary hint: grounding describes the EXTRACTION's evidence, so it
    // says nothing once the specialist has chosen the value themselves.
    const showUngroundedHint = Boolean(props.typeUngrounded) && !edited;
    const secondaryTooltip =
        (props.secondaryTypeLabel
            ? `This document also supported "${props.secondaryTypeLabel}". `
            : "This document supported more than one request type. ")
        + `${selectedType?.name ? `"${selectedType.name}"` : "The pre-selected type"} was the stronger `
        + "match, so it is pre-selected - but because the document supported more than one request "
        + "type this row is not treated as confirmed. Review both and confirm the correct one; the "
        + "alternative is shown for information only and was not selected.";
    // Amber tooltip: explain WHY the purpose is uncertain. Names the extracted purpose + resolved type
    // so the specialist understands it isn't a valid child of that type (or that none was found).
    const uncertainTooltip = props.extractedPurpose
        ? `The document's request purpose "${props.extractedPurpose}" is not a valid purpose for `
          + `${selectedType?.name ? `the "${selectedType.name}" request type` : "this request type"} `
          + `in ROIS. Select a valid purpose from the list.`
        : `No request purpose belonging to `
          + `${selectedType?.name ? `the "${selectedType.name}" request type` : "this request type"} `
          + `was found in the document. Select one from the list.`;
    // Red "type not exact" hint: the AI extracted a type that isn't an exact catalog match, so it can't
    // be auto-selected. Show the free text (red, non-selectable) until the specialist picks a type.
    const extractedTypeText = [props.extractedType, props.extractedPurpose].filter(Boolean).join(" / ");
    const showTypeNotExact = (props.typeNotExact ?? false) && !edited && !props.value && Boolean(extractedTypeText);
    const typeNotExactTooltip = `The AI extracted "${extractedTypeText}", which is not an exact match to a `
        + `request type in ROIS, so it can't be auto-selected. Choose the correct request type from the list.`;
    const compact = props.quickEntryCompact ?? false;
    // AB#35076: Quick Entry now mimics Classic's red "Purpose Type is required." once a Type is
    // chosen and no Purpose is. Supersedes AB#35098's amber "Valid type - select a purpose" for
    // this state: amber reads as advisory, and it left the row's own Confirm button enabled, so it
    // could never satisfy "must not be able to confirm without a purpose".
    //
    // Quick Entry only. Classic's own call site drives this from real redux-form meta (the error
    // in the card's screenshot), and DropdownField lets meta.error win over errMsg anyway, so this
    // neither duplicates nor fights it.
    const showPurposeRequired = compact && Boolean(props.value) && !props.purposeValue;
    // AB#38739 / AB#38158 consistency: the row is amber for every request now, whatever the
    // extraction reported, so when no cause-specific hint applies it still has to say WHY. The
    // mapper puts exactly this text on row.statusExplanation, but that is rendered only on the
    // COLLAPSED row - and suppressed there once the row is settled - so while the row is open,
    // which is the whole time it is amber, the reason was never displayed to anyone. Surfaced
    // through the field for the same reason typeUngrounded is (see that prop's docblock).
    //
    // Quick Entry only (`compact`): the standing amber is a Quick Entry rule, and Classic Mode's
    // Details tab has no row status to explain. NOT suppressed on `edited`, unlike the hints above:
    // those describe the extraction's evidence and stop applying once the specialist picks a value,
    // whereas this rule applies to the fields themselves however the value got there.
    const showAlwaysReviewHint = compact
        && !showSecondaryCandidate && !showUngroundedHint && !showTypeNotExact
        && !showPurposeUncertain && !showPurposeRequired;
    const typeLabel = compact ? "Type" : "What is the request type?";
    const purposeLabel = compact ? "Purpose" : "What is the purpose of the request?";
    // Required-ness is already conveyed by Quick Entry's own row status
    // icon/color (QuickEntryFieldGroupList's data-status accent) - the
    // demo mockup's compact columns carry no per-field asterisk, unlike
    // Classic Mode's stacked layout.
    const showRequiredAsterisk = !compact;

    const typeClassName = [
        compact ? "data-entry request-type qe-field" : "data-entry request-type",
        showTypeNotExact ? "request-type--unresolved" : "",
        showSecondaryCandidate ? "request-type--ambiguous" : "",
    ].filter(Boolean).join(" ");
    const typeField = (
        <div className={typeClassName}>
            <DropdownField
                label={typeLabel}
                required={showRequiredAsterisk}
                meta={toDropdownFieldMeta(props.meta)}
                options={[
                    { label: "- Select -", value: 0, disabled: true, hidden: true },
                    ...(props.requestTypes?.map(type => ({ label: type.name, value: type.id })) ?? []),
                ]}
                onChange={onChangeType}
                value={props.value ?? DEFAULT_DROPDOWN_VALUE}
            />
            {showSecondaryCandidate ? (
                // Amber ⚠ + ⓘ explaining WHY this row is amber: the document supported a second type.
                // role="status" announces it without stealing focus; no PHI - catalog names only.
                <span className="request-type__ambiguous-hint" role="status">
                    <span aria-hidden="true">⚠ </span>
                    {props.secondaryTypeLabel
                        ? `Also matched ${props.secondaryTypeLabel}`
                        : "More than one request type matched"}
                    <span
                        className="request-type__ambiguous-info"
                        title={secondaryTooltip}
                        role="img"
                        aria-label={secondaryTooltip}
                    >{" "}&#9432;</span>
                </span>
            ) : null}
            {showUngroundedHint ? (
                // AB#37979: WHY this row is amber - the type is in the dropdown but the document does
                // not verifiably say it. Rendered here rather than via statusExplanation for the
                // reason given on the typeUngrounded prop. role="status" announces it without
                // stealing focus; no PHI - catalog names only.
                <span className="request-type__ambiguous-hint" role="status">
                    <span aria-hidden="true">&#9888; </span>
                    Not verified against the document
                    <span
                        className="request-type__ambiguous-info"
                        title={UNGROUNDED_HINT_TOOLTIP}
                        role="img"
                        aria-label={UNGROUNDED_HINT_TOOLTIP}
                    >{" "}&#9432;</span>
                </span>
            ) : null}
            {showTypeNotExact ? (
                // Red, non-selectable hint showing the AI free text that didn't resolve to a catalog type.
                <span className="request-type__unresolved-hint" role="img" title={typeNotExactTooltip} aria-label={typeNotExactTooltip}>
                    <span aria-hidden="true">⚠ </span>AI found "{extractedTypeText}" — not a listed type
                </span>
            ) : null}
        </div>
    );
    const purposeClassName = [
        "data-entry purpose-type",
        compact ? "qe-field" : "",
        showPurposeUncertain && !showPurposeRequired ? "purpose-type--uncertain" : "",
    ].filter(Boolean).join(" ");
    const purposeField = (
        <div className={purposeClassName}>
            <DropdownField
                label={purposeLabel}
                required={showRequiredAsterisk}
                meta={toDropdownFieldMeta(props.purposeMeta)}
                options={[
                    { label: "- Select -", value: 0, disabled: true, hidden: true },
                    ...(selectedType?.requestPurpose.map(purpose => ({ label: purpose.name, value: purpose.id })) ?? []),
                ]}
                onChange={onChangePurpose}
                disabled={!props.value}
                value={props.purposeValue ?? DEFAULT_DROPDOWN_VALUE}
                errMsg={showPurposeRequired ? "Purpose Type is required." : undefined}
            />
            {showPurposeUncertain && !showPurposeRequired ? (
                // Amber ⚠ hint reusing the QE amber token ($qe-amber, .purpose-type--uncertain in
                // QuickEntryFieldGroupList.scss). role="status" so the prompt is announced without
                // stealing focus. No PHI — static guidance text only.
                <span className="purpose-type__uncertain-hint" role="status">
                    <span aria-hidden="true">⚠ </span>Valid type — select a purpose
                    <span
                        className="purpose-type__uncertain-info"
                        title={uncertainTooltip}
                        role="img"
                        aria-label={uncertainTooltip}
                    >{" "}&#9432;</span>
                </span>
            ) : null}
        </div>
    );

    if (compact) {
        // Quick Entry-only wrapper (master-demo-v6.html's own
        // .qe-row__value--cols class) that lays Type/Purpose out side by
        // side - safe to add here because Classic Mode never sets
        // quickEntryCompact and therefore never renders this wrapper (see
        // the Fragment branch below for why Classic Mode itself can't have
        // a wrapper div).
        return (
            <>
                <div className="qe-row__value--cols">
                    {typeField}
                    {purposeField}
                </div>
            {showAlwaysReviewHint ? (
                // AB#38739: WHY this row is amber when nothing is wrong with the extraction. Short
                // label on the row, full reason in the ⓘ tooltip. Placed OUTSIDE .qe-row__value--cols
                // rather than inside typeField, where the cause-specific hints live: those are about
                // the type, this one is about both fields, and a child of a 50%-width column wraps it
                // to three lines (measured on a local build). Its own class carries the styling
                // because the shared hint styles are nested under .request-type--ambiguous, which is
                // on the type column only. role="status" announces it without stealing focus; no PHI
                // - fixed wording, no document text.
                <span className="request-type__always-review-hint request-type__ambiguous-hint" role="status">
                    <span aria-hidden="true">&#9888; </span>
                    Always review type and purpose before confirming
                    <span
                        className="request-type__ambiguous-info"
                        title={ALWAYS_REVIEW_EXPLANATION}
                        role="img"
                        aria-label={ALWAYS_REVIEW_EXPLANATION}
                    >{" "}&#9432;</span>
                </span>
            ) : null}
                {showChip ? <span className="qe-chip--ai">AI pre-selected</span> : null}
            </>
        );
    }

    return (
        // Fragment, not a wrapping div: .data-entry's flex-basis: 50% rule
        // (FormField.scss) only applies when it's a DIRECT child of the
        // surrounding flex container (e.g. .details-tab-right-pane in
        // classic RightPane) - an extra wrapper div here breaks that
        // adjacency and collapses the side-by-side layout to stacked full
        // width (visually confirmed via local vs. test-environment
        // screenshot comparison).
        <>
            {typeField}
            {purposeField}
            {showChip ? <span className="qe-chip--ai">AI pre-selected</span> : null}
        </>
    );
};

export default RequestTypePurposeField;

// Exported so DataEntryDetails.tsx can key its Quick Entry renderRowContent
// special-case off the same constant used to build the row (AC11).
export const REQUEST_TYPE_PURPOSE_ROW_ID = "requestTypePurpose";

export interface RequestTypePurposeFormValues {
    readonly requestType?: number;
    readonly requestPurpose?: number;
    readonly requestTypes?: readonly RequestType[];
}

/**
 * Builds the FieldGroupRow for the Request Type/Purpose row from a
 * RequestAnalysisData fixture (Spec #34098 / F1a, AC5/Test 1.1). Request
 * Type/Purpose is always a required field, so an unmapped fixture yields
 * FieldGroupRowStatus.NeedsEntry; a mapped/populated one yields Confirmed
 * (no confidence-dot scoring here - that's F1b-rois's (#34100) domain).
 *
 * formValues is optional (appended, not inserted, to avoid breaking the
 * existing 2/3-arg test call sites) - without it this behaves exactly as
 * before, AI-data-only. Quick Entry passes it so the row also flips to
 * Confirmed once the specialist picks a value manually, not only when the
 * AI extraction itself returns one - the same live-form-value pattern
 * AuthorizationDateField/RequestDateField/CaseNumberField/
 * ClinicResponsibleField's mappers already use.
 */
export function mapRequestTypePurposeToFieldGroupRow(
    data: RequestAnalysisData | undefined,
    badgeNumber: number,
    isEdited: boolean = false,
    formValues?: RequestTypePurposeFormValues,
    // AB#35076: requestAnalysis.confidence.requestPurpose. Read-time only (absent on a persisted
    // re-read), and optional so the AI-data-only 2/3-arg call sites are untouched.
    purposeConfidence?: GroundingState,
    // AB#37979: requestAnalysis.confidence.requestType, same read-time-only caveat. Joe Widi's
    // ruling (2026-08-18) grades this row on catalog membership AND grounding, so the TYPE's own
    // grounding verdict now has to reach the tier decision - previously only the purpose's did,
    // and then only to decide whether the purpose was usable at all.
    typeConfidence?: GroundingState,
    // AB#38739 amendment: the specialist has SETTLED this row - confirmed or acknowledged it. Green is
    // unreachable from the extraction alone (see the tier decision below), so green can now only ever
    // mean "a person confirmed this", which is a stronger claim than the one it used to make. Appended
    // and optional, so every existing call site keeps its behaviour.
    settled?: boolean,
): FieldGroupRow {
    const hasAiValue = data?.requestTypeMapped !== undefined && data?.requestTypeMapped !== null;
    // BOTH dropdowns must be set - a Type with no Purpose yet is still an
    // incomplete/NeedsEntry row, not Confirmed (the Purpose dropdown is
    // itself disabled until Type is chosen, so Type-only is a real,
    // reachable intermediate state, not a hypothetical).
    const hasFormValue = Boolean(formValues?.requestType) && Boolean(formValues?.requestPurpose);
    // AB#35076: a TYPE alone is not a confirmable row. hasAiValue is true on requestTypeMapped
    // only, so the Confirmed branch below used to be reachable with an empty Purpose - which is
    // exactly what "Insurance / Attorney" produced: Insurance resolved, Attorney is not one of its
    // purposes, and the row went green over a blank dropdown. The only thing that had stood in the
    // way was the requestPurposeUncertain FLAG, and a flag is the wrong guarantee for this - it has
    // to hold no matter which resolver produced the data. Require a purpose from one source or the
    // other, structurally.
    //
    // AB#35076 (regression): requiring a purpose to be PRESENT is still not enough. That held while
    // an unresolvable purpose arrived as null - the "Insurance / Attorney" case above - but the
    // extraction now also returns a purpose id that IS set and is NOT a child of the returned type.
    // Presence alone then reads as a complete pair: hasAiPurpose true, so the row confirms, and the
    // server's amber flag cannot save it either because it derives as
    // `typeMapped.HasValue && !purposeMapped.HasValue` (ResolvingRequestAnalysisRetriever) - false
    // when the id is set but wrong. The row went green over a Purpose the dropdown does not even
    // offer, since its options are that type's own children.
    //
    // So check membership, not presence. The catalog is already on formValues.requestTypes for the
    // label resolution below; read child membership from `type.requestPurpose`, never from the
    // deprecated `purpose.typeID`. When no catalog is supplied (the AI-data-only 2/3-arg call sites)
    // fall back to the presence check rather than inventing a failure - absent catalog is "cannot
    // validate", never "invalid".
    // Membership is not sufficient: QA's three cases each substituted a VALID child of the right type
    // for the purpose the document stated ("Insurance / Attorney" -> Insurance / Claim). Nor is the
    // extracted text usable - the API reports the RESOLVED name there, so it always agrees with itself.
    // The discriminator is the per-field grounding: InferredNoSource means the extraction invented the
    // value. One shared rule in requestTypePurposeResolver, so the row and the form cannot disagree.
    const mappedPurposeIsUsable = (): boolean => {
        const catalog = formValues?.requestTypes;
        if (!catalog || catalog.length === 0) {
            // The AI-data-only 2/3-arg call sites supply no catalog, so MEMBERSHIP is unknowable here
            // and must not be read as invalid. The extraction's own verdict needs no catalog though,
            // so it still applies - this is the one branch the shared rule cannot answer.
            return purposeConfidence !== GroundingState.InferredNoSource;
        }
        const mappedType = catalog.find(type => type.id === data?.requestTypeMapped);
        return isMappedPurposeUsable(data?.requestPurposeMapped, mappedType, purposeConfidence);
    };
    const hasAiPurpose = data?.requestPurposeMapped !== undefined
        && data?.requestPurposeMapped !== null
        && mappedPurposeIsUsable();
    // AB#37976: hasAiValue/hasAiPurpose read the ORIGINAL STATIC AI payload and never change for the
    // life of the transaction, so once an extraction supplied both a type and a usable purpose this
    // branch was permanently true - even after the specialist picked a different Type in the live
    // dropdown (which resets the live Purpose to 0, see onChangeType above) without ever picking a new
    // Purpose. Gate the AI branch on the live form still matching the AI's own suggestion.
    //
    // This is a SEPARATE question from AB#35076's mappedPurposeIsUsable above and both must hold:
    // that one asks "is the AI's purpose usable at all" (a real child of the AI's type, and not
    // InferredNoSource); this one asks "does the live form still say what the AI said". A usable AI
    // pair the specialist has since moved away from is still not a confirmable row.
    // Ask whether a live value has actually DIVERGED, not whether formValues exists. Presence is no
    // longer a proxy for "there is a live form": AB#35076 made the catalog itself arrive on
    // formValues.requestTypes, so mappedPurposeIsUsable's callers legitimately pass
    // `{ requestTypes }` with both dropdowns still empty. Reading absent dropdowns as a mismatch
    // regressed two of AB#35076's own tests ("a Grounded purpose still confirms" and its
    // child-of-mapped-type sibling), which is the correct signal - an unanswered dropdown is not the
    // specialist contradicting the AI.
    //
    // Truthiness-checked before comparing, exactly as liveTypeContradictsAi does below. AB#37976's
    // reported state still gates off: picking a different Type sets a live requestType that differs,
    // and onChangeType resets the live Purpose to 0 (falsy) rather than to a rival id.
    const liveTypeDiverged = Boolean(formValues?.requestType)
        && formValues?.requestType !== data?.requestTypeMapped;
    const livePurposeDiverged = Boolean(formValues?.requestPurpose)
        && formValues?.requestPurpose !== data?.requestPurposeMapped;
    const matchesUneditedAiSuggestion = !liveTypeDiverged && !livePurposeDiverged;
    const hasMappedValue = (hasAiValue && hasAiPurpose && matchesUneditedAiSuggestion) || hasFormValue;

    // Amber / purpose-uncertain (TDD §11): a valid Type resolved but the extracted purpose was not a
    // valid child of it. Until the specialist picks a purpose (form value), the row is NOT Confirmed —
    // it sits in the amber (LowConfidence) attention tier. Only fires when the API flagged it; the new
    // field defaults undefined, so pre-amber fixtures are unaffected.
    const purposeUncertain = Boolean(data?.requestPurposeUncertain) && !hasFormValue;
    // AB#35075: the document plausibly supported a SECOND catalog type. A pre-selection the document
    // itself does not settle cannot read as Confirmed (green) - green asserts "this is the answer",
    // which would be overstating it and is exactly the reported problem. The row therefore sits in the
    // amber (LowConfidence) attention tier until the specialist confirms, which in this app is
    // acknowledge-satisfiable - so the note stays NON-BLOCKING per the PRD rather than gating
    // submission. Suppressed once the specialist has picked both dropdowns themselves (hasFormValue),
    // because at that point the choice is theirs, not the AI's.
    // Resolve the secondary's display label from the catalog by ID. The ids are what PERSIST
    // (tblRequestIntakeData 160035/160036), so resolving from them keeps the label correct on a plain
    // reopen too; the contract's raw name strings are only present on a fresh extraction and are used
    // as a fallback when no catalog is supplied (Classic/legacy call sites).
    const secondaryFromCatalog = (() => {
        const type = formValues?.requestTypes?.find(t => t.id === data?.secondaryRequestTypeMapped);
        if (!type) {
            return null;
        }
        const purpose = type.requestPurpose.find(p => p.id === data?.secondaryRequestPurposeMapped);
        return [type.name, purpose?.name].filter(Boolean).join(" / ");
    })();
    const secondaryLabelResolved = secondaryFromCatalog
        ?? ([data?.secondaryRequestType, data?.secondaryRequestPurpose].filter(Boolean).join(" / ") || null);
    const hasSecondaryReturned =
        data?.secondaryRequestTypeMapped != null || Boolean(data?.secondaryRequestType);

    // AB#35074: the specialist has overridden the AI suggestion once the live
    // form value diverges from the AI-mapped ids. Quick Entry now SEEDS the form
    // WITH the AI-mapped Type/Purpose (seedExtractedQuickEntryFields), so
    // "form differs from AI" is the reliable edited signal - and the read-only
    // row must then show the CURRENT form value, not the stale AI text (the bug:
    // the old `if (hasAiValue)`-first branch made the specialist's correction
    // unreachable, so the row reverted to the AI value after Confirm while the
    // sidebar showed the correction). Only reached when formValues is supplied
    // (Quick Entry) - Classic/legacy 2/3-arg callers keep the AI-only behavior.
    const editedFromAi = hasFormValue && (
        formValues?.requestType !== data?.requestTypeMapped
        || formValues?.requestPurpose !== data?.requestPurposeMapped
    );

    // AB#35075: amber while the row still shows the AI's UNEDITED suggestion. Gating on !hasFormValue
    // was wrong: Quick Entry SEEDS the form with the AI-mapped type/purpose (seedExtractedQuickEntryFields,
    // added with the AB#35098 auto-select), so hasFormValue is true immediately and amber never fired -
    // the row stayed green, which is the reported bug. editedFromAi is the reliable "the specialist chose
    // this" signal, for the same reason AB#35074 introduced it.
    const hasSecondaryCandidate = hasSecondaryReturned && !editedFromAi;

    // AB#37979: the second amber cause, from Joe Widi's ruling on the bug (2026-08-18) - the
    // confidence a specialist sees for THIS row is catalog membership plus grounding:
    //
    //   green  - the value is in the dropdown AND grounded in the document
    //   amber  - in the dropdown but NOT grounded, OR more than one value was considered
    //   red    - not in the dropdown and not grounded
    //
    // "More than one value considered" is hasSecondaryCandidate above (AB#35075). What was missing
    // is the ungrounded half: a catalog-resolved type/purpose the extraction could not tie to any
    // text in the document went straight to Confirmed, so the row asserted "this is the answer"
    // over a value nothing in the request supports. That is the reported complaint - a value shown
    // green and "AI pre-selected" that the document does not actually establish.
    //
    // Only an EXPLICIT verdict counts. The confidence map is read-time only (AC11) and absent on a
    // persisted re-read, so `undefined` means "not assessed" and must stay green - reading absence
    // as ungrounded would turn every reopened transaction amber.
    //
    // WHICH STATE MEANS "not grounded" IS COUNTER-INTUITIVE FOR THESE TWO FIELDS - read this before
    // changing the condition. RequestType/RequestPurpose are declared with CiteIfAvailable: true and
    // are not grounding-exempt, so LocatedGroundingResolver.Resolve can only ever hand them:
    //
    //   Grounded          - cited span located, value verified against it, box computed
    //   InferredNoSource  - PRESENT but unverified: no cited quote, or the quote did not locate, or
    //                       the value did not verify against the covering text (all route through
    //                       Unsourced(citeIfAvailable: true), which returns InferredNoSource)
    //   NotGrounded       - the value was ABSENT/whitespace (the first branch of Resolve)
    //
    // So for this row "in the dropdown but not grounded" - Joe Widi's amber - is InferredNoSource,
    // NOT NotGrounded. NotGrounded here means "the extraction returned nothing", which cannot
    // coexist with a catalog-mapped value and is handled by the structural red path instead.
    // The first cut of this had it backwards and read NotGrounded for the purpose; that branch was
    // unreachable, and the test that "covered" it passed a payload the API cannot produce (a mapped
    // purpose alongside NotGrounded).
    //
    // The PURPOSE contributes nothing to amber, deliberately: its present-but-unverified state is
    // exactly the InferredNoSource that mappedPurposeIsUsable routes to RED (AB#35076, confirmed
    // 2026-08-18 - an invented value must be re-picked, not acknowledged), and its absent state is
    // red structurally. Amber is therefore driven by the TYPE alone.
    const typeUngrounded = typeConfidence === GroundingState.InferredNoSource;
    // Gated on the AI having made the claim and the specialist not having moved off it: grounding
    // describes the extraction's evidence, so it says nothing about a value the specialist chose.
    //
    // TYPE-SCOPED, not editedFromAi (David Heifetz, PR !9995 — Important 1). editedFromAi is an OR
    // across BOTH dropdowns, so changing only the PURPOSE cleared this amber while the ungrounded
    // TYPE was untouched — the row went green over exactly the classification the amber exists to
    // flag, which is the reported complaint arriving through another door. This amber is about the
    // TYPE's grounding (typeUngrounded reads typeConfidence), so the "specialist moved off it"
    // predicate has to be type-scoped as well: liveTypeDiverged, the same one AB#37976 uses.
    const classificationUngrounded = hasAiValue && !liveTypeDiverged && typeUngrounded;

    // Resolve the human-readable "Type / Purpose" label from the live form ids
    // against the requestTypes list (shared by the edited-AI path and the pure
    // manual-entry path - no duplication).
    const resolveFormValueLabel = (): string | null => {
        const selectedType = formValues?.requestTypes?.find(type => type.id === formValues?.requestType);
        const selectedPurpose = selectedType?.requestPurpose.find(purpose => purpose.id === formValues?.requestPurpose);
        return [selectedType?.name, selectedPurpose?.name].filter(Boolean).join(" / ") || null;
    };

    let value: string | null = null;
    // MERGE (AB#35098 amber + AB#35074 correction): order matters. purposeUncertain FIRST (it implies
    // hasAiValue but must win); then our edited-from-AI correction; then the unedited AI value; then pure
    // manual entry; then the feature's "type not an exact catalog match" free-text (red) branch. The
    // amber and edited branches are mutually exclusive (purposeUncertain requires !hasFormValue,
    // editedFromAi requires hasFormValue).
    if (purposeUncertain || (hasAiValue && !hasAiPurpose)) {
        // Show the resolved Type ONLY - the purpose is deliberately not rendered, because nothing valid
        // was auto-selected for it.
        //
        // AB#35076 (review 53055, Nelson Omuto): this used to key on the server's purposeUncertain flag
        // alone, which is false when the extraction returns an id it INFERRED. The row then fell through
        // to the branch below and displayed the invented purpose - "Insurance / Claim" for a document
        // that says Attorney - under the AI PRE-SELECTED chip. So we refused to auto-select it while
        // still asserting on screen that the AI found it. Keying on !hasAiPurpose covers unmapped and
        // inferred alike, so a purpose we would not seed is never shown as an extracted value either.
        value = data?.requestType ?? (data?.requestTypeMapped != null ? String(data.requestTypeMapped) : null);
    } else if (editedFromAi) {
        // AB#35074: specialist corrected an AI suggestion - show the correction.
        value = resolveFormValueLabel();
    } else if (hasAiValue) {
        // Unedited AI value - show the AI's own text verbatim.
        value = [data?.requestType, data?.requestPurpose].filter(Boolean).join(" / ") || String(data?.requestTypeMapped);
    } else if (hasFormValue) {
        // Pure manual entry, no AI suggestion - resolve names from the form ids (shared helper).
        value = resolveFormValueLabel();
    } else if (data?.requestType || data?.requestPurpose) {
        // AB#35098: Request Type not an exact catalog match - show the AI-extracted free text in the
        // (red) NeedsEntry row so the specialist sees what was found. Not auto-selectable (not a catalog
        // option; confirmed design: show-as-red, not selectable).
        value = [data?.requestType, data?.requestPurpose].filter(Boolean).join(" / ") || null;
    }

    let status: FieldGroupRowStatus;
    // Two amber reasons used to sit in one condition here: AB#35098's purposeUncertain and AB#35075's
    // hasSecondaryCandidate. AB#35076 splits them, because they are not the same kind of doubt.
    //
    // purposeUncertain is GONE from the amber tier. It fired on exactly "type resolved, no valid
    // purpose" and won ahead of the structural check, so the row rendered amber with its Confirm
    // button live (isFieldGroupRowConfirmDisabled spares LowConfidence by design) over an empty
    // Purpose. Dev decision is that this state mimics Classic: red and un-confirmable. hasMappedValue
    // now requires a purpose, so it reaches NeedsEntry on its own. purposeUncertain still drives the
    // VALUE above (show the resolved Type only, never a guessed purpose) - only the status changed.
    //
    // hasSecondaryCandidate (AB#35075) STAYS amber and is unaffected: there the type and purpose are
    // both settled, and the doubt is only whether a rival type fit better - acknowledge-satisfiable,
    // non-blocking per the PRD.
    //
    // Order matters, and it is the reverse of what it was: the structural check runs FIRST so a row
    // missing its purpose is red even when a secondary candidate is also present. Leaving amber ahead
    // of it would let that new path hand back a confirmable row with a blank Purpose - the same bug
    // through a different door.
    // AB#38739: Confirmed is UNREACHABLE for this row. A usable pair is always amber, so the
    // specialist double-checks the classification on every transaction, whatever the model reported.
    //
    // This replaces a widening series of exceptions to green with the rule they were converging on.
    // Each earlier amber cause was added after a specialist had already been misled by a green row:
    // AB#35075 (a rival catalog type also fit) and AB#37979 (Joe Widi, 2026-08-18 - in the dropdown
    // but not grounded). Joe's ruling stops one step short of this by still allowing green for a
    // grounded, unrivalled value; that last case is what this removes. Request Type and Purpose drive
    // downstream routing and billing, they are the two fields most often INFERRED rather than
    // transcribed off the page, and nothing numeric sits behind the green - the LowConfidence tier
    // names a confidence threshold that does not exist anywhere in the client (AB#38709).
    //
    // hasSecondaryCandidate and classificationUngrounded are deliberately still computed: they no
    // longer pick the tier, but they still pick the EXPLANATION below, which is now the only thing
    // that distinguishes "amber because a rival type fit" from "amber because we always check this".
    // Deleting them would take the specialist's reason away with them.
    //
    // The structural red check still runs FIRST and is untouched - a row with no usable purpose is
    // NeedsEntry and un-confirmable, not amber. Amber here stays acknowledge-satisfiable and
    // non-blocking per the PRD, so this costs one deliberate click and gates no submission.
    if (!hasMappedValue) {
        status = FieldGroupRowStatus.NeedsEntry;
    } else if (settled) {
        // AB#38739 amendment: green AFTER the specialist has confirmed or acknowledged the row, never
        // before. This is not a return to the old behaviour - the extraction on its own can no longer
        // reach this branch, so green has changed meaning from "the AI is confident" to "a person
        // checked it". That is the whole point of the amber rule, and it is what gives the specialist
        // feedback that they have dealt with the row.
        //
        // It also restores the Feature 2 signal by the front door: ValueAccepted and
        // ConfidenceStateAtEdit derive from `status === Confirmed`, so a settled row is recorded as
        // reviewed without the correction payload needing to be told separately.
        status = FieldGroupRowStatus.Confirmed;
    } else {
        status = FieldGroupRowStatus.LowConfidence;
    }

    // AB#35075: surface the reason on the COLLAPSED row too - the specialist should not have to click
    // Edit to learn why a row is amber. Catalog names only, no PHI.
    // AB#35075: the explanation must appear whenever the row is amber for this reason - it must NOT
    // depend on resolving the alternative's NAME. If the catalog lookup cannot name it (e.g. the user's
    // request-type list does not include that type), still explain WHY the row is not confirmed and
    // simply omit the name, rather than silently showing no explanation at all.
    // AB#37976 (review round 1): the chip must not claim credit for a Type the AI did not choose.
    // editedFromAi is gated on hasFormValue, which is FALSE in exactly the state this bug's gate now
    // produces - a live Type with the Purpose reset to 0 by onChangeType - so a row whose live Type
    // had already diverged from the AI's still rendered "AI pre-selected" beside dropdowns the AI
    // never selected. The field's own `edited` latch hides the chip after ITS OWN onChange, but not
    // on a fresh render of an already-diverged form (a persisted value under AB#35146, a rehydrate,
    // or a remount), which is the state the reporter photographed.
    // Keyed on the TYPE only, and truthiness-checked before comparing, so the amber
    // "valid type / no valid purpose" state - where the AI's type IS what is selected - keeps its chip.
    const liveTypeContradictsAi = hasAiValue
        && Boolean(formValues?.requestType)
        && formValues?.requestType !== data?.requestTypeMapped;
    const primaryLabel = [data?.requestType, data?.requestPurpose].filter(Boolean).join(" / ");
    // AB#37979: say WHICH value is unsupported so the specialist knows where to look. Catalog names
    // only, never document text - no PHI on the collapsed row (AC12). Only the request type can
    // reach this branch (see above), so there is one subject and no default to invent.
    const UNGROUNDED_EXPLANATION =
        "The pre-selected request type is a valid option, but it could not be matched to any text "
        + "in this document, so this row is not treated as confirmed - check it against the request "
        + "and confirm the correct value.";
    const statusExplanation = hasSecondaryCandidate
        ? (secondaryLabelResolved
            ? `This document also supported "${secondaryLabelResolved}". `
            : "This document supported more than one request type. ")
          + (primaryLabel ? `"${primaryLabel}" was the stronger match and is pre-selected, but ` : "The stronger match is pre-selected, but ")
          + "because more than one request type fit, this row is not treated as confirmed - review it and "
          + "confirm the correct one. The alternative is shown for information only and was not selected."
        // AB#37979: the secondary reason wins when both apply - "another type also fit" is more
        // specific and more actionable than "we could not find it in the document". The
        // classificationUngrounded gate stays: it also carries the hasAiValue / !editedFromAi
        // conditions, which the subject flags alone do not.
        : classificationUngrounded
        ? UNGROUNDED_EXPLANATION
        // AB#38739: the row is now amber even when neither specific doubt applies, so it needs a
        // reason of its own. Amber with no explanation is worse than green was - the specialist sees
        // the attention tier and has nothing to act on, and would reasonably read it as a fault.
        // Say plainly that this is a standing rule about these two fields, not a signal that the
        // extraction went wrong. Catalog names only, never document text - no PHI (AC12).
        // AB#38739 amendment: once the row is settled there is nothing left to prompt for, so the
        // standing-rule reason goes away with the amber. A cause-specific reason above still shows,
        // because "another type also fit" is worth keeping on the record after the fact.
        : settled ? undefined
        : ALWAYS_REVIEW_EXPLANATION;

    return {
        id: REQUEST_TYPE_PURPOSE_ROW_ID,
        badgeNumber,
        status,
        statusExplanation,
        label: "Request Type & Purpose",
        value,
        // Drop the "AI pre-selected" chip once the specialist has overridden the
        // AI value (AB#35074 - it used to persist alongside the reverted value).
        // AB#35076 (review 53134, David Heifetz #2) asked for hasAiPurpose here too, because the chip
        // was rendering over a blank Purpose. Deliberately NOT doing that: he observed it while the row
        // still DISPLAYED the invented purpose ("Insurance / Claim"), and the value branch above now
        // shows the Type alone in that state. The chip describes what is on screen, and the Type on
        // screen genuinely was AI pre-selected - which is also the existing pinned decision ("Type is
        // still an AI suggestion", the AB#35076 invalid-purpose test). Gating on hasAiPurpose would
        // flip that deliberate call and drop the only signal that the Type came from the document.
        // AB#37976 (review round 1) adds !liveTypeContradictsAi, which is orthogonal to the call
        // above: that one is about hasAiPurpose, this one about the live TYPE having diverged.
        isAiPreSelected: hasAiValue && !isEdited && !editedFromAi && !liveTypeContradictsAi,

    };
}

// AB#35066: the composite Request Type/Purpose row carries TWO values, so its per-value
// grounding icon must be decided PER SUB-VALUE (not once for the whole row like the
// scalar rows' isAiPreSelected). This pure helper resolves the human-readable label and
// the "doc-sourced" flag for each sub-value independently, so a row where the specialist
// corrected only the Purpose still shows the icon on the (unchanged) Type.
export interface RequestTypePurposeGrounding {
    /** Resolved Request Type name from the live form id, or null if unresolved. */
    readonly typeLabel: string | null;
    /** Resolved Request Purpose name from the live form id, or null if unresolved. */
    readonly purposeLabel: string | null;
    /** Type is doc-sourced iff an AI value exists and the live form id still equals it. */
    readonly typeDocSourced: boolean;
    /** Purpose is doc-sourced iff an AI value exists and the live form id still equals it. */
    readonly purposeDocSourced: boolean;
}

/**
 * Per-sub-value grounding descriptor for the Request Type/Purpose row (AB#35066).
 *
 * "Doc-sourced" mirrors the scalar rows' rule ("current value equals the AI-extracted
 * value, unedited") but applied to each dropdown on its own: Type is doc-sourced iff the
 * live form Type id equals data.requestTypeMapped; Purpose iff the live form Purpose id
 * equals data.requestPurposeMapped. Labels resolve from the live form ids against the
 * requestTypes list (the same canonical names the dropdowns show), so the split read-only
 * display stays consistent with the editable control.
 */
export function resolveRequestTypePurposeGrounding(
    data: RequestAnalysisData | undefined,
    formValues: RequestTypePurposeFormValues | undefined,
): RequestTypePurposeGrounding {
    const selectedType = formValues?.requestTypes?.find(type => type.id === formValues?.requestType);
    const selectedPurpose = selectedType?.requestPurpose.find(purpose => purpose.id === formValues?.requestPurpose);
    const typeDocSourced = data?.requestTypeMapped !== undefined
        && data?.requestTypeMapped !== null
        && formValues?.requestType === data.requestTypeMapped;
    const purposeDocSourced = data?.requestPurposeMapped !== undefined
        && data?.requestPurposeMapped !== null
        && formValues?.requestPurpose === data.requestPurposeMapped;
    return {
        typeLabel: selectedType?.name ?? null,
        purposeLabel: selectedPurpose?.name ?? null,
        typeDocSourced,
        purposeDocSourced,
    };
}
